import { useState } from 'react';
import styled from '@/styles';
import Popover from '@/components/Popover';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { categoryColor } from './tierColors';
import PopoverSurface from './PopoverSurface';
import { CheckCircleIcon, ExclamationTriangleIcon } from './icons';

const Row = styled('div', {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
});
const BadgeChip = styled('span', {
  // UA-button resets FIRST (the `font` shorthand would otherwise clobber the
  // fontSize/fontWeight below): the pools chip renders `as="button"` so it is
  // reachable by keyboard; these are visual no-ops for the span usages.
  background: 'none',
  font: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  fontSize: '$sm',
  fontWeight: 700,
  border: '1px solid transparent',
  variants: {
    kind: {
      positive: {
        color: dependabilityV2Colors.depPositive,
        borderColor: dependabilityV2Colors.depFeedPositive,
      },
      danger: {
        color: dependabilityV2Colors.depTierMonitored,
        backgroundColor: dependabilityV2Colors.depDangerBg,
      },
      warning: {
        color: dependabilityV2Colors.depWarnText,
        backgroundColor: dependabilityV2Colors.depWarnBg,
      },
      // "0 preferred pools" is neither good nor bad — an explicit variant so
      // the empty state is a deliberate grey chip rather than whatever the
      // unstyled base happens to inherit.
      neutral: {
        color: dependabilityV2Colors.depInk,
        borderColor: dependabilityV2Colors.depBorder,
      },
    },
  },
});
const PoolList = styled(PopoverSurface, { padding: '12px', minWidth: '200px' });

const StatusIcon = ({ positive }: { positive: boolean }) =>
  positive ? <CheckCircleIcon /> : <ExclamationTriangleIcon />;

interface Props {
  badges: DependabilityV2['badges'];
}

const StatusBadgeRow = ({ badges }: Props) => {
  const [poolsOpen, setPoolsOpen] = useState(false);
  const noShowPositive = badges.no_shows_count === 0;
  const latePositive = badges.late_count === 0;
  const windowLabel = `(${badges.window_days}D)`;
  // preferred_pools can list the same customer twice (favorited AND LTA — the
  // backend has no unique index across kinds), which inflates the "N preferred
  // pools" count and collides React keys. Collapse to one entry per customer_id.
  const uniquePools = Array.from(
    new Map(
      badges.preferred_pools.map((pool) => [pool.customer_id, pool]),
    ).values(),
  );

  return (
    <Row>
      <BadgeChip kind={noShowPositive ? 'positive' : 'danger'}>
        <StatusIcon positive={noShowPositive} />
        {noShowPositive
          ? 'No recent no-shows'
          : `${badges.no_shows_count} no-show${
              badges.no_shows_count === 1 ? '' : 's'
            } ${windowLabel}`}
      </BadgeChip>
      <span style={{ position: 'relative' }}>
        {/* A real <button> (fix round 3): the pool list was unreachable by
            keyboard as a styled span with onClick. As a native button the
            existing window clickout also gives click-again-to-close for free. */}
        {/* Zero pools is an ABSENCE, not an achievement: styled positive with a
            green check it read as a good signal, sitting beside two badges
            where green genuinely means "clean". Neutral when there are none. */}
        <BadgeChip
          as="button"
          type="button"
          kind={uniquePools.length > 0 ? 'positive' : 'neutral'}
          css={{ cursor: 'pointer' }}
          onClick={() => setPoolsOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={poolsOpen}
          aria-controls="preferred-pools-popover"
          data-testid="preferred-pools-badge"
        >
          {uniquePools.length > 0 && <StatusIcon positive />}
          {`${uniquePools.length} preferred pool${
            uniquePools.length === 1 ? '' : 's'
          }`}
        </BadgeChip>
        <Popover
          id="preferred-pools-popover"
          isOpen={poolsOpen}
          onRequestClose={() => setPoolsOpen(false)}
        >
          <PoolList>
            {uniquePools.length === 0 && <div>No active pool memberships</div>}
            {uniquePools.map((pool) => (
              <div
                key={pool.customer_id}
                style={{ padding: '4px 0', color: categoryColor('neutral') }}
              >
                {pool.customer_name || `Client #${pool.customer_id}`}
              </div>
            ))}
          </PoolList>
        </Popover>
      </span>
      <BadgeChip kind={latePositive ? 'positive' : 'warning'}>
        <StatusIcon positive={latePositive} />
        {`${badges.late_count} late ${windowLabel}`}
      </BadgeChip>
    </Row>
  );
};

export default StatusBadgeRow;
